/**
 * Where a candidatura stands, in the words an employer and a recruiter both use.
 *
 * `collections/talent_applications.ts` declares these as the `status` enum and
 * the employer dashboard on the public site offers them in its dropdown. The
 * site used to keep its own list — "Aplicado", "Rechazado", "En Revisión" and a
 * "Pendiente" the column cannot hold — while the enum it wrote into had
 * "Entrevista", which the dropdown did not offer at all. So an employer could
 * choose a value the database refused, and could never choose one it accepted.
 *
 * One list, then, and the site imports it rather than copying it. Deliberately
 * free of imports, like `job_labels.ts` beside it: the admin panel bundles this
 * into the browser, the backend loads it in Node and the Astro site bundles it
 * too, and a vocabulary that one of the three cannot reach is not shared.
 * `tests/shared-config-imports.test.mjs` holds every module the site reaches
 * into here to that.
 *
 * The order is the order a decision moves, which is also the order a dropdown
 * should read in.
 */

export type ApplicationStatus = "applied" | "reviewing" | "interview" | "rejected" | "hired";

export interface ApplicationStatusOption {
    id: ApplicationStatus;
    label: string;
    /**
     * The admin panel's chip colour — a Rebase chip key, spelled as the five
     * literals in use so the collection can take this list as its enum without
     * this file importing Rebase's type for it. The site does not colour by it.
     */
    color: "blue" | "orange" | "purple" | "red" | "green";
}

export const APPLICATION_STATUSES: readonly ApplicationStatusOption[] = [
    { id: "applied", label: "Sin contestar", color: "blue" },
    { id: "reviewing", label: "En revisión", color: "orange" },
    { id: "interview", label: "Entrevista", color: "purple" },
    { id: "rejected", label: "Descartado", color: "red" },
    { id: "hired", label: "Contratado", color: "green" }
];

export const APPLICATION_STATUS_LABEL = Object.fromEntries(
    APPLICATION_STATUSES.map(option => [option.id, option.label])
) as Record<ApplicationStatus, string>;

/** A value the column can actually hold. Anything else is somebody's typo or an old spelling. */
export function isApplicationStatus(value: unknown): value is ApplicationStatus {
    return typeof value === "string" && value in APPLICATION_STATUS_LABEL;
}

/**
 * Nobody has answered this candidate yet.
 *
 * The one state an employer's list is sorted by and counted by, so it gets a
 * name rather than a string comparison repeated at each call site. A row with
 * no status at all is `applied`: that is the column's default and the only
 * value the apply button ever writes.
 */
export function isUnanswered(status: unknown): boolean {
    return status == null || status === "" || status === "applied";
}
